import React from "react";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import closeGrayIcon from "@src/assets/svg/close-icon-gray.svg";
import thingsTwinsIcon from "@src/assets/svg/things-twins-blue.svg";
import closedPasswordEye from "@src/assets/svg/password-eye-closed-icon.svg";
import openPasswordEye from "@src/assets/svg/password-eye-open-icon.svg";
import {
  DeviceAuthOption,
  deviceAuthTabOptions,
} from "@src/types/device-auth-option";
import {
  DeviceSecurityMode,
  deviceSecurityModes,
} from "@src/types/device-security-mode";
import {
  DeviceSecurityPolicy,
  deviceSecurityPolicies,
} from "@src/types/device-security-policy";
import { AutoGenerateCertificateSetting } from "@src/components/atoms/auto-generate-certificate-setting/auto-generate-certificate-setting";
import { DropAreaAddSourceGateway } from "@src/components/atoms/drop-area-add-source-gateway/drop-area-add-source-gateway";
import { HelpIconPopover } from "@src/components/atoms/help-icon-popover/help-icon-popover";
import { IS_OPC_UA_SKILL_MANDATORY } from "@src/cloud-connect/utils/onboarding-operation";
import { ToolTip } from "@src/components/atoms/tooltip/tooltip";
import { useAddNewMachineDrawerModalContext } from "./add-new-machine-drawer-modal-context";
import styles from "./add-new-machine-drawer-modal.module.scss";

const cssClassName = "add-new-machine-drawer-modal";

const twinTypeLabels: Record<string, string> = {
  OPCUA: "OPC UA",
  RPC: "RPC",
};

export const TwinSkillCard = () => {
  const {
    texts,
    machineSkillsTexts,
    twinTypeTabs,
    isTwinExpanded,
    twinPort,
    rpcDsn,
    selectedTwinType,
    opcUaSettings,
    isAutoGenerateEnabled,
    showReuseGeneratedCertificateHint,
    displayUserLoginPasswordAsText,
    showTwinPortAvailable,
    setIsTwinExpanded,
    setTwinPort,
    setRpcDsn,
    setSelectedTwinType,
    setOpcUaSettings,
    toggleAutoGenerateCertificate,
    handlePasswordButton,
  } = useAddNewMachineDrawerModalContext();

  const authTabTypeLabels = {
    [DeviceAuthOption.ANONYMOUS]: machineSkillsTexts.anonymousTab,
    [DeviceAuthOption.USER_LOGIN]: machineSkillsTexts.userLoginTab,
    [DeviceAuthOption.CLIENT_CERTIFICATE]:
      machineSkillsTexts.clientCertificateTab,
  };

  // While OPC UA is mandatory the card cannot be collapsed and its skill type
  // cannot be switched, so every RPC branch below is unreachable.

  return (
    <div className={styles["skill-card"]}>
      <div className={styles["skill-card-header"]}>
        <div className={styles["skill-card-header-left"]}>
          <img
            src={thingsTwinsIcon}
            alt={machineSkillsTexts.thingsTwinTitle}
            className={styles["skill-card-icon"]}
          />
          <h4 className={styles["skill-card-title"]}>
            {machineSkillsTexts.thingsTwinTitle}
          </h4>
        </div>
        <ToolTip
          content={machineSkillsTexts.opcUaRequiredTooltip}
          position="top"
        >
          <span>
            <button
              type="button"
              className={styles["toggle-skill-button"]}
              disabled={IS_OPC_UA_SKILL_MANDATORY}
              onClick={() => setIsTwinExpanded(!isTwinExpanded)}
            >
              <span className={styles["toggle-skill-button-symbol"]}>
                {isTwinExpanded ? "−" : "+"}
              </span>
            </button>
          </span>
        </ToolTip>
      </div>
      <div className={styles["skill-card-content"]}>
        <p className={styles["skill-card-description"]}>
          {machineSkillsTexts.thingsTwinDescription}
        </p>
      </div>

      {isTwinExpanded && (
        <div className={styles["skill-card-expanded-content"]}>
          <FlexRowContainer horizontalGap={8} justifyContent="space-between">
            <label className={styles[`${cssClassName}__label`]}>
              {machineSkillsTexts.portSettingsLabel}
            </label>
            <HelpIconPopover
              helpLabel={texts.help}
              helpText={
                selectedTwinType === "RPC"
                  ? machineSkillsTexts.rpcPortHelpText
                  : machineSkillsTexts.opcUaPortHelpText
              }
            />
          </FlexRowContainer>
          <div
            className={`${styles["skill-type-options"]} ${
              twinTypeTabs.length === 1
                ? styles["skill-type-options-single"]
                : ""
            }`}
          >
            {twinTypeTabs.map((type) => (
              <button
                key={type}
                type="button"
                className={`${styles["skill-type-tab"]} ${
                  selectedTwinType === type
                    ? styles["skill-type-tab-active"]
                    : ""
                }`}
                onClick={() => setSelectedTwinType(type)}
                disabled={IS_OPC_UA_SKILL_MANDATORY && type !== "OPCUA"}
              >
                {twinTypeLabels[type] || type}
              </button>
            ))}
          </div>

          {selectedTwinType === "OPCUA" && (
            <FlexColumnContainer verticalGap={16}>
              <div className={styles[`${cssClassName}__input-container`]}>
                <div className={styles[`${cssClassName}__input-wrapper`]}>
                  <input
                    type="text"
                    value={twinPort}
                    className={styles[`${cssClassName}__input`]}
                    onChange={(e) => setTwinPort(e.target.value)}
                  />
                  {twinPort && (
                    <button
                      type="button"
                      className={styles["port-clear-button"]}
                      onClick={() => setTwinPort("")}
                    >
                      <img src={closeGrayIcon} alt="" />
                    </button>
                  )}
                </div>
                {showTwinPortAvailable && (
                  <p className={styles["port-available-text"]}>
                    {machineSkillsTexts.portIsAvailableLabel}
                  </p>
                )}
              </div>

              <FlexRowContainer horizontalGap={8}>
                <div className={styles[`${cssClassName}__dropdown-container`]}>
                  <label className={styles[`${cssClassName}__dropdown-label`]}>
                    <span
                      className={styles[`${cssClassName}__dropdown-label-text`]}
                    >
                      {machineSkillsTexts.securityModeLabel}
                    </span>
                    <select
                      value={opcUaSettings.securityMode}
                      onChange={(e) =>
                        setOpcUaSettings({
                          ...opcUaSettings,
                          securityMode: e.target.value as DeviceSecurityMode,
                        })
                      }
                      className={styles[`${cssClassName}__dropdown`]}
                    >
                      {deviceSecurityModes.map((mode) => (
                        <option key={mode} value={mode}>
                          {mode}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className={styles[`${cssClassName}__dropdown-container`]}>
                  <label className={styles[`${cssClassName}__dropdown-label`]}>
                    <span
                      className={styles[`${cssClassName}__dropdown-label-text`]}
                    >
                      {machineSkillsTexts.securityPolicyLabel}
                    </span>
                    <select
                      value={opcUaSettings.securityPolicy}
                      onChange={(e) =>
                        setOpcUaSettings({
                          ...opcUaSettings,
                          securityPolicy: e.target
                            .value as DeviceSecurityPolicy,
                        })
                      }
                      className={styles[`${cssClassName}__dropdown`]}
                    >
                      {deviceSecurityPolicies.map((policy) => (
                        <option key={policy} value={policy}>
                          {policy}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </FlexRowContainer>

              <div className={styles[`${cssClassName}__tabs-container`]}>
                <label className={styles[`${cssClassName}__tabs-label`]}>
                  {machineSkillsTexts.authenticationTabsLabel}
                </label>

                {deviceAuthTabOptions.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() =>
                      setOpcUaSettings({
                        ...opcUaSettings,
                        authOption: tab,
                      })
                    }
                    className={`${styles[`${cssClassName}__tab-button`]} ${
                      opcUaSettings.authOption === tab
                        ? styles[`${cssClassName}__tab-button--active`]
                        : ""
                    }`}
                  >
                    {authTabTypeLabels[tab]}
                  </button>
                ))}
              </div>

              {opcUaSettings.authOption === DeviceAuthOption.USER_LOGIN && (
                <FlexColumnContainer verticalGap={16}>
                  <div className={styles[`${cssClassName}__input-container`]}>
                    <label className={styles[`${cssClassName}__label`]}>
                      {machineSkillsTexts.usernameLabel}
                    </label>
                    <div className={styles[`${cssClassName}__input-wrapper`]}>
                      <input
                        type="text"
                        value={opcUaSettings.username}
                        className={styles[`${cssClassName}__input`]}
                        onChange={(e) =>
                          setOpcUaSettings({
                            ...opcUaSettings,
                            username: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className={styles[`${cssClassName}__input-container`]}>
                    <label className={styles[`${cssClassName}__label`]}>
                      {machineSkillsTexts.passwordLabel}
                    </label>
                    <div className={styles[`${cssClassName}__input-wrapper`]}>
                      <input
                        type={
                          displayUserLoginPasswordAsText ? "text" : "password"
                        }
                        value={opcUaSettings.password}
                        className={styles[`${cssClassName}__input`]}
                        onChange={(e) =>
                          setOpcUaSettings({
                            ...opcUaSettings,
                            password: e.target.value,
                          })
                        }
                      />
                      <IconButton
                        fun={(e) => handlePasswordButton(e)}
                        icon={
                          displayUserLoginPasswordAsText
                            ? closedPasswordEye
                            : openPasswordEye
                        }
                      />
                    </div>
                  </div>
                </FlexColumnContainer>
              )}

              {opcUaSettings.authOption ===
                DeviceAuthOption.CLIENT_CERTIFICATE && (
                <FlexColumnContainer verticalGap={16}>
                  <AutoGenerateCertificateSetting
                    isAutoGenerateEnabled={isAutoGenerateEnabled}
                    onToggle={toggleAutoGenerateCertificate}
                    showReuseGeneratedCertificateHint={
                      showReuseGeneratedCertificateHint
                    }
                  />
                  {!isAutoGenerateEnabled && (
                    <>
                      <DropAreaAddSourceGateway<
                        typeof opcUaSettings,
                        "clientCertificateBase64"
                      >
                        label={machineSkillsTexts.clientCertificateLabel}
                        values={opcUaSettings}
                        keyProp="clientCertificateBase64"
                        setValues={setOpcUaSettings}
                      />
                      <DropAreaAddSourceGateway<
                        typeof opcUaSettings,
                        "clientCertificatePrivateKeyBase64"
                      >
                        label={
                          machineSkillsTexts.clientCertificatePrivateKeyLabel
                        }
                        values={opcUaSettings}
                        keyProp="clientCertificatePrivateKeyBase64"
                        setValues={setOpcUaSettings}
                      />
                      <div
                        className={styles[`${cssClassName}__input-container`]}
                      >
                        <label className={styles[`${cssClassName}__label`]}>
                          {machineSkillsTexts.clientCertificatePasswordLabel}
                        </label>
                        <div
                          className={styles[`${cssClassName}__input-wrapper`]}
                        >
                          <input
                            type={
                              displayUserLoginPasswordAsText
                                ? "text"
                                : "password"
                            }
                            value={opcUaSettings.clientCertificatePassword}
                            className={styles[`${cssClassName}__input`]}
                            onChange={(e) =>
                              setOpcUaSettings({
                                ...opcUaSettings,
                                clientCertificatePassword: e.target.value,
                              })
                            }
                          />
                          <IconButton
                            fun={(e) => handlePasswordButton(e)}
                            icon={
                              displayUserLoginPasswordAsText
                                ? closedPasswordEye
                                : openPasswordEye
                            }
                          />
                        </div>
                      </div>
                      <DropAreaAddSourceGateway<
                        typeof opcUaSettings,
                        "serverPublicKeyBase64"
                      >
                        label={machineSkillsTexts.serverPublicKeyLabel}
                        values={opcUaSettings}
                        keyProp="serverPublicKeyBase64"
                        setValues={setOpcUaSettings}
                      />
                    </>
                  )}
                </FlexColumnContainer>
              )}
            </FlexColumnContainer>
          )}

          {selectedTwinType === "RPC" && (
            // Unreachable while IS_OPC_UA_SKILL_MANDATORY is true.
            <FlexColumnContainer verticalGap={16}>
              <div className={styles[`${cssClassName}__input-container`]}>
                <label className={styles[`${cssClassName}__label`]}>
                  {machineSkillsTexts.rpcDsnLabel}
                </label>
                <div className={styles[`${cssClassName}__input-wrapper`]}>
                  <span className={styles[`${cssClassName}__dsn-prefix`]}>
                    rpc://
                  </span>
                  <input
                    type="text"
                    value={rpcDsn}
                    className={styles[`${cssClassName}__input`]}
                    onChange={(e) => setRpcDsn(e.target.value)}
                  />
                </div>
              </div>
            </FlexColumnContainer>
          )}
        </div>
      )}
    </div>
  );
};
